// React
import { memo } from "react";

// Servisni sloj
import { formatUnitPrice } from "services/api/pricing/consts";

// Tipovi
import { SchemeAdjustmentSummaryProps, SchemeSummaryCardProps } from "./types";

/** Promjena u riječima: postotak nosi predznak, fiksni iznos valutu. */
const formatAdjustment = (props: SchemeAdjustmentSummaryProps): string => {
  const { isAbsolute, value } = props;

  if (value === null) return "—";
  if (isAbsolute) return formatUnitPrice.format(value);

  const sign = value > 0 ? "+" : "";

  return `${sign}${value.toFixed(2).replace(".", ",").replace(",00", "")} %`;
};

/**
 * Lijeva ploha čarobnjaka za grupu. Puni se dok se grupa upisuje: naziv i opis s prvog
 * koraka, stavke s drugog.
 */
const SchemeSummaryCard: React.FC<SchemeSummaryCardProps> = (props: SchemeSummaryCardProps) => {
  const { formValues, tenantCount } = props;

  const items = formValues.items.filter((item) => item.keyCode !== null);

  return (
    <div className="bg-blue-600 h-full w-full min-w-0 max-w-full overflow-hidden flex flex-col items-center justify-center p-8">
      <div className="text-center">
        <h1 className="text-4xl font-bold tracking-tight text-white">
          {formValues.name || "Nova grupa"}
        </h1>

        {formValues.description && (
          <p className="text-blue-100 mt-3 max-w-md mx-auto">{formValues.description}</p>
        )}

        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          <span className="rounded-full bg-white/15 px-4 py-1.5 text-sm text-white">
            {formValues.isActive ? "Nudi se novim klijentima" : "Ne nudi se novim klijentima"}
          </span>

          {tenantCount !== null && (
            <span className="rounded-full bg-white/15 px-4 py-1.5 text-sm text-white">
              {tenantCount} {tenantCount === 1 ? "klijent" : "klijenata"}
            </span>
          )}
        </div>
      </div>

      <div className="mt-8 w-full min-w-0 max-w-full overflow-x-auto overflow-y-hidden">
        {items.length === 0 ? (
          <p className="text-blue-100 text-sm text-center">
            Bez ijedne stavke klijent plaća standardne cijene iz cjenika.
          </p>
        ) : (
          /*
            Kartice dijele jednu plohu i razdvaja ih tanka crta, isto kao paketi u čarobnjaku
            za cjenik. `w-max mx-auto` ih centrira dok stanu, a kasnije se ploha klizne.
          */
          <div className="w-max mx-auto flex divide-x divide-slate-200 bg-white rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5 snap-x snap-mandatory">
            {items.map((item, index) => {
              const isAbsolute = item.adjustmentType === "Absolute";

              return (
                <div
                  key={index}
                  className="w-[220px] flex-shrink-0 snap-start bg-white p-6 flex flex-col"
                >
                  <div className="text-[13px] text-slate-500 break-all min-h-[36px]">
                    {item.keyCode}
                  </div>

                  <div className="mt-4 text-[32px] leading-none font-bold tracking-tight text-slate-900">
                    {formatAdjustment({
                      keyCode: item.keyCode,
                      isAbsolute,
                      value: item.value,
                    })}
                  </div>

                  <div className="mt-2 text-[12px] text-slate-400">
                    {isAbsolute ? "fiksno, ne prati cjenik" : "postotak, prati cjenik"}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(SchemeSummaryCard);
